import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("ember-chat-session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("ember-chat-session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const C = t.chat;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg, language: lang }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let failed = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          let data;
          try {
            data = JSON.parse(chunk.slice(6));
          } catch {
            continue;
          }
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.booking) {
            setMessages((m) => [...m, { role: "booking", booking: data.booking }]);
          }
          if (data.booking_error) {
            const errText = data.booking_error === "slot_taken" ? C.errSlotTaken : C.errInvalid;
            setMessages((m) => [...m, { role: "assistant", content: errText }]);
          }
          if (data.error) failed = true;
        }
      }
      if (failed) throw new Error("stream error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        if (!copy[copy.length - 1].content) copy[copy.length - 1] = { role: "assistant", content: C.error };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-gradient-to-br from-[#ff6b00] to-[#ffb700] text-white flex items-center justify-center shadow-[0_8px_30px_rgba(255,107,0,0.45)] hover:scale-105 transition-transform duration-200"
        aria-label="Chat"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "x" : "chat"}
            initial={{ opacity: 0, rotate: -40 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 40 }}
            transition={{ duration: 0.18 }}
            className="flex"
          >
            {open ? <X size={24} /> : <MessageCircle size={24} />}
          </motion.span>
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[5.5rem] right-5 z-[90] w-[min(400px,calc(100vw-2.5rem))] h-[560px] max-h-[calc(100vh-8rem)] rounded-2xl overflow-hidden border border-white/10 bg-neutral-950 shadow-2xl shadow-black/40 flex flex-col"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/[0.04]">
              <img src="/logos/emberline-final-512.png" alt="Ember" className="w-10 h-10 object-contain" />
              <div className="flex-1">
                <p className="font-tech text-xs tracking-[0.2em] uppercase text-white">{C.title}</p>
                <p className="flex items-center gap-1.5 mt-0.5 text-[11px] text-white/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {C.status}
                </p>
              </div>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white/[0.07] border border-white/10 px-4 py-3 text-sm text-white/85 leading-relaxed">
                {C.welcome}
              </div>
              {messages.map((m, i) =>
                m.role === "booking" ? (
                  <motion.div
                    key={i}
                    data-testid={`chat-booking-card-${i}`}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-[90%] rounded-2xl border border-[#ff6b00]/50 bg-gradient-to-br from-[#ff6b00]/15 to-[#ffd700]/10 px-4 py-4"
                  >
                    <p className="flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[#ffb066]">
                      <CheckCircle2 size={15} className="text-[#ff6b00]" /> {C.bookedTitle}
                    </p>
                    <p className="mt-2 font-display italic text-xl text-white">
                      {m.booking.date} — <span className="not-italic font-tech text-base text-gradient-fire">{m.booking.time_slot}</span>
                    </p>
                    <p className="mt-1 text-xs text-white/60">{m.booking.service} · {m.booking.name}</p>
                  </motion.div>
                ) : (
                  <div
                    key={i}
                    data-testid={`chat-message-${i}`}
                    className={
                      m.role === "user"
                        ? "ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-gradient-to-r from-[#ff6b00] to-[#ff9500] px-4 py-3 text-sm text-white leading-relaxed"
                        : "max-w-[85%] rounded-2xl rounded-tl-md bg-white/[0.07] border border-white/10 px-4 py-3 text-sm text-white/85 leading-relaxed whitespace-pre-wrap"
                    }
                  >
                    {m.content || (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:0.15s]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce [animation-delay:0.3s]" />
                      </span>
                    )}
                  </div>
                )
              )}
              {messages.length === 0 && (
                <div className="pt-2 flex flex-col items-start gap-2">
                  {C.suggestions.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#ff6b00]/50 bg-[#ff6b00]/10 px-4 py-2 text-xs text-[#ffb066] hover:bg-[#ff6b00]/25 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10 bg-white/[0.03]"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={C.placeholder}
                className="flex-1 rounded-full bg-white/[0.06] border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#ff6b00] focus:outline-none transition-colors"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={busy || !input.trim()}
                className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-[#ff6b00] to-[#ffb700] text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
